// composables
import useApiRequest from '@/composables/useApiRequest'
// utilities
import { alertToast } from '@/utilities/notification'

interface ApiManufacturer {
  id: number
  name: string
  regions: string[]
  accentColor: string
  logoUrl: string
  squareLogoUrl: string
  isActive: boolean
}

type DealershipAllowed = {
  id: number
  name: string
  manufacturers: number[]
}

type GroupedDealership = {
  manufacturer: string
  dealerships: Array<{
    label: string
    value: number
  }>
}

/**
 * Groups dealerships by manufacturer.
 * Fetches manufacturers from the API and organizes the provided dealerships accordingly.
 *
 * @param {DealershipAllowed[]} dealerships - Array of dealerships to group by manufacturer
 * @returns {Promise<GroupedDealership[]>} Array of grouped dealerships by manufacturer
 */
async function groupDealershipsByManufacturer(dealerships: DealershipAllowed[]): Promise<GroupedDealership[]> {
  const manufacturerRequest = useApiRequest()
  const grouped: Record<string, Array<{ label: string; value: number }>> = {}

  try {
    const response = await manufacturerRequest.send({ endpoint: '/v1/manufacturers' })

    if (response.success) {
      const manufacturers: ApiManufacturer[] = response.data
      const manufacturerMap: Record<number, string> = {}
      manufacturers.forEach((m: ApiManufacturer) => {
        manufacturerMap[m.id] = m.name
      })

      dealerships.forEach(dealership => {
        const manufacturersList: number[] = dealership.manufacturers?.length ? dealership.manufacturers : []

        if (manufacturersList.length === 0) {
          // Handle case where dealership has no manufacturers
          const manufacturerName = 'Other'
          if (!grouped[manufacturerName]) {
            grouped[manufacturerName] = []
          }
          grouped[manufacturerName].push({
            label: dealership.name,
            value: dealership.id
          })
        } else {
          manufacturersList.forEach((manufacturerId: number): void => {
            const manufacturerName: string = manufacturerMap[manufacturerId] || 'Other'
            if (!grouped[manufacturerName]) {
              grouped[manufacturerName] = []
            }

            grouped[manufacturerName].push({
              label: dealership.name,
              value: dealership.id
            })
          })
        }
      })

      return Object.entries(grouped).map(([manufacturer, dealerships]) => ({
        manufacturer,
        dealerships
      }))
    } else {
      alertToast('Failed to fetch manufacturers', response.message, 'error')
    }
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
    alertToast('Failed to fetch manufacturers', errorMessage, 'error')
  }

  return []
}

export default groupDealershipsByManufacturer
